import React, { useState, useEffect } from 'react';
import {
  User,
  Building2,
  Briefcase,
  Target,
  MessageSquare,
  Brain,
  Save,
  Check,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { toast } from '../Toast';
import { registerDirty } from '../../lib/dirtyGuard';
import './AIPersonalization.css';

const COMPANY_TYPES = [
  { value: 'vendor', label: 'Technology Vendor' },
  { value: 'integrator', label: 'Systems Integrator' },
  { value: 'customer', label: 'Enterprise Customer' },
  { value: 'analyst', label: 'Industry Analyst' },
  { value: 'government', label: 'Government Agency' },
  { value: 'partner', label: 'Channel Partner' },
];

const ROLE_FUNCTIONS = [
  { value: 'sales', label: 'Sales' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'executive', label: 'Executive' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'security', label: 'Security' },
  { value: 'it', label: 'IT' },
  { value: 'other', label: 'Other' },
];

const INDUSTRIES = [
  'Financial Services',
  'Healthcare',
  'Government',
  'Education',
  'Retail',
  'Manufacturing',
  'Technology',
  'Energy & Utilities',
  'Telecommunications',
  'Other',
];

const COMPETITORS = [
  'CrowdStrike',
  'Microsoft',
  'Cisco',
  'Fortinet',
  'Zscaler',
  'SentinelOne',
  'Splunk',
  'IBM',
  'Trend Micro',
  'Check Point',
];

const COMMUNICATION_TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'technical', label: 'Technical' },
  { value: 'executive', label: 'Executive Summary' },
];

const RESPONSE_LENGTHS = [
  { value: 'concise', label: 'Concise' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'detailed', label: 'Detailed' },
];

function AIPersonalization() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  // Unsaved-edit tracking: while dirty, the Settings shell confirms before
  // any navigation that would discard this form (slice 4 dirty guard).
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) return undefined;
    return registerDirty('AI personalization');
  }, [dirty]);

  const [context, setContext] = useState({
    company_name: '',
    company_type: '',
    industry: '',
    role_title: '',
    role_function: '',
    competitive_focus: [],
    product_focus: [],
    territory: {
      regions: [],
      verticals: [],
      named_accounts: [],
    },
    communication_preferences: {
      tone: 'professional',
      length: 'balanced',
      format: 'bullets',
    },
    ai_personalization: {
      proactive_alerts: true,
      auto_research: true,
      memory_retention: '1year',
    },
    onboarding_completed: false,
  });

  useEffect(() => {
    loadContext();
  }, []);

  const loadContext = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/user-context');
      if (data.exists) {
        setContext(prev => ({
          ...prev,
          ...data.context,
          territory: data.context.territory || prev.territory,
          communication_preferences: data.context.communication_preferences || prev.communication_preferences,
          ai_personalization: data.context.ai_personalization || prev.ai_personalization,
        }));
      }
    } catch (err) {
      console.error('Failed to load user context:', err);
      setError('Failed to load your preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiClient.put('/api/user-context', context);
      setDirty(false);
      setSaved(true);
      toast('Preferences saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save context:', err);
      setError('Failed to save your preferences');
      toast('Couldn’t save your preferences — try again', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContext(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    setDirty(true);
  };

  const updateNestedField = (parent, field, value) => {
    setContext(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
    setSaved(false);
    setDirty(true);
  };

  const toggleCompetitor = (competitor) => {
    setContext(prev => ({
      ...prev,
      competitive_focus: prev.competitive_focus.includes(competitor)
        ? prev.competitive_focus.filter(c => c !== competitor)
        : [...prev.competitive_focus, competitor],
    }));
    setSaved(false);
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="ai-personalization">
        <div className="ai-p-loading">
          <div className="ai-p-spinner" />
          <span>Loading your preferences...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-personalization">
      <header className="ai-p-header">
        <div className="ai-p-header-icon">
          <Sparkles size={24} />
        </div>
        <div>
          <h1>AI Personalization</h1>
          <p>Configure how the AI assistant understands and responds to you</p>
        </div>
      </header>

      {error && (
        <div className="ai-p-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="ai-p-sections">
        {/* Company Context */}
        <section className="ai-p-section">
          <div className="ai-p-section-header">
            <Building2 size={20} />
            <h2>Company Context</h2>
          </div>
          <p className="ai-p-section-desc">
            Help the AI understand your organization and perspective
          </p>

          <div className="ai-p-form-grid">
            <div className="ai-p-field">
              <label>Company Name</label>
              <input
                type="text"
                value={context.company_name || ''}
                onChange={(e) => updateField('company_name', e.target.value)}
                placeholder="e.g., Cisco, Palo Alto Networks"
              />
            </div>

            <div className="ai-p-field">
              <label>Company Type</label>
              <select
                value={context.company_type || ''}
                onChange={(e) => updateField('company_type', e.target.value)}
              >
                <option value="">Select type...</option>
                {COMPANY_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="ai-p-field">
              <label>Industry</label>
              <select
                value={context.industry || ''}
                onChange={(e) => updateField('industry', e.target.value)}
              >
                <option value="">Select industry...</option>
                {INDUSTRIES.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Role & Function */}
        <section className="ai-p-section">
          <div className="ai-p-section-header">
            <Briefcase size={20} />
            <h2>Your Role</h2>
          </div>
          <p className="ai-p-section-desc">
            The AI will tailor responses to your function and responsibilities
          </p>

          <div className="ai-p-form-grid">
            <div className="ai-p-field">
              <label>Job Title</label>
              <input
                type="text"
                value={context.role_title || ''}
                onChange={(e) => updateField('role_title', e.target.value)}
                placeholder="e.g., Senior Sales Engineer"
              />
            </div>

            <div className="ai-p-field">
              <label>Function</label>
              <select
                value={context.role_function || ''}
                onChange={(e) => updateField('role_function', e.target.value)}
              >
                <option value="">Select function...</option>
                {ROLE_FUNCTIONS.map(func => (
                  <option key={func.value} value={func.value}>{func.label}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Competitive Focus */}
        <section className="ai-p-section">
          <div className="ai-p-section-header">
            <Target size={20} />
            <h2>Competitive Focus</h2>
          </div>
          <p className="ai-p-section-desc">
            Select competitors you frequently encounter or want to track
          </p>

          <div className="ai-p-chips">
            {COMPETITORS.map(competitor => (
              <button
                key={competitor}
                className={`ai-p-chip ${context.competitive_focus?.includes(competitor) ? 'active' : ''}`}
                onClick={() => toggleCompetitor(competitor)}
              >
                {competitor}
                {context.competitive_focus?.includes(competitor) && <Check size={14} />}
              </button>
            ))}
          </div>
        </section>

        {/* Communication Preferences */}
        <section className="ai-p-section">
          <div className="ai-p-section-header">
            <MessageSquare size={20} />
            <h2>Communication Preferences</h2>
          </div>
          <p className="ai-p-section-desc">
            How should the AI communicate with you?
          </p>

          <div className="ai-p-form-grid">
            <div className="ai-p-field">
              <label>Tone</label>
              <select
                value={context.communication_preferences?.tone || 'professional'}
                onChange={(e) => updateNestedField('communication_preferences', 'tone', e.target.value)}
              >
                {COMMUNICATION_TONES.map(tone => (
                  <option key={tone.value} value={tone.value}>{tone.label}</option>
                ))}
              </select>
            </div>

            <div className="ai-p-field">
              <label>Response Length</label>
              <select
                value={context.communication_preferences?.length || 'balanced'}
                onChange={(e) => updateNestedField('communication_preferences', 'length', e.target.value)}
              >
                {RESPONSE_LENGTHS.map(len => (
                  <option key={len.value} value={len.value}>{len.label}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* AI Behavior */}
        <section className="ai-p-section">
          <div className="ai-p-section-header">
            <Brain size={20} />
            <h2>AI Behavior</h2>
          </div>
          <p className="ai-p-section-desc">
            Control how proactive the AI should be
          </p>

          <div className="ai-p-toggles">
            <label className="ai-p-toggle">
              <input
                type="checkbox"
                checked={context.ai_personalization?.proactive_alerts ?? true}
                onChange={(e) => updateNestedField('ai_personalization', 'proactive_alerts', e.target.checked)}
              />
              <span className="ai-p-toggle-slider" />
              <div className="ai-p-toggle-label">
                <span>Proactive Alerts</span>
                <span className="ai-p-toggle-desc">Get notified about relevant updates</span>
              </div>
            </label>

            <label className="ai-p-toggle">
              <input
                type="checkbox"
                checked={context.ai_personalization?.auto_research ?? true}
                onChange={(e) => updateNestedField('ai_personalization', 'auto_research', e.target.checked)}
              />
              <span className="ai-p-toggle-slider" />
              <div className="ai-p-toggle-label">
                <span>Auto Research</span>
                <span className="ai-p-toggle-desc">AI proactively researches relevant topics</span>
              </div>
            </label>
          </div>

          <div className="ai-p-field" style={{ marginTop: 'var(--space-4)' }}>
            <label>Memory Retention</label>
            <select
              value={context.ai_personalization?.memory_retention || '1year'}
              onChange={(e) => updateNestedField('ai_personalization', 'memory_retention', e.target.value)}
            >
              <option value="30days">30 Days</option>
              <option value="90days">90 Days</option>
              <option value="1year">1 Year</option>
              <option value="forever">Forever</option>
            </select>
            <span className="ai-p-field-hint">How long should the AI remember your conversations?</span>
          </div>
        </section>
      </div>

      {/* Save Button */}
      <div className="ai-p-actions">
        <button
          className={`ai-p-save-btn ${saved ? 'saved' : ''}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="ai-p-spinner small" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check size={18} />
              Saved
            </>
          ) : (
            <>
              <Save size={18} />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default AIPersonalization;
